import React, { useState, useEffect } from "react";
import { Doughnut } from "react-chartjs-2";
import EnquieryForm from "../loanComponents/EnquieryForm";
import LoginModal from "../Login/LoginModal";
import { useNavigate } from "react-router-dom";
import { IsAuthenticated, IsNormalUser } from "../../../utils/AccessTokenStore";

const InputSlider = ({
  label,
  name,
  min,
  max,
  step,
  value,
  onChange,
  minval,
  maxval,
}) => {
  const handleBlur = () => {
    // Ensure the value is within the specified range
    const newValue = Math.min(Math.max(parseFloat(value), min), max);
    onChange({ target: { name, value: newValue } });
  };
  return (
    <div>
      <div className="w-full flex flex-row  justify-between">
        <label
          htmlFor="default-range"
          className="block mb-1 text-sm font-medium text-gray-900  "
        >
          {label}
        </label>

        <input
          type="number"
          name={name}
          className="border-logo-orange border-opacity-50 border w-36 text-right px-1 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-logo-orange appearance-none"
          value={value}
          min={min}
          max={max}
          onChange={onChange}
          onBlur={handleBlur}
        />
      </div>
      <input
        id="default-range"
        name={name}
        type="range"
        min={min}
        step={step}
        max={max}
        value={value}
        onChange={onChange}
        className="w-full h-2 bg-gray-200 rounded-lg  cursor-pointer appearance-none "
      />
      <div className="w-full flex flex-row justify-between">
        <p className="text-xs text-slate-700 font-semibold">{minval}</p>
        <p className="text-xs text-slate-700  font-semibold">{maxval}</p>
      </div>
    </div>
  );
};

const calculateAmortizationSchedule = (principal, rate, tenureYears) => {
  const monthlyRate = rate / 12 / 100;
  const tenureMonths = tenureYears * 12;
  const emi =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths)) /
    (Math.pow(1 + monthlyRate, tenureMonths) - 1);

  let openingBalance = parseFloat(principal);
  let amortizationData = [];

  for (let month = 1; month <= tenureMonths; month++) {
    const interestPayment = openingBalance * monthlyRate;
    const principalPayment = emi - interestPayment;
    const closingBalance = openingBalance - principalPayment;

    amortizationData.push({
      month,
      openingBalance: openingBalance.toFixed(2),
      emi: emi.toFixed(2),
      principalRepaid: principalPayment.toFixed(2),
      interestPaid: interestPayment.toFixed(2),
      closingBalance: closingBalance.toFixed(2),
    });

    openingBalance = closingBalance;
  }

  return amortizationData;
};

const CalculatorEnquiery = ({ updateAmortizationTableData }) => {
  const [isloginModalOpen, setIsloginModalOpen] = useState(false);
  const navigate = useNavigate();
  const NavigateToLink = (data) => {
    const route = data.route;
    if (route) {
      navigate(route);
    } else {
      if (IsAuthenticated() && IsNormalUser()) {
        navigate(`/user/dashboard`);
      } else {
        setIsloginModalOpen(true);
      }
    }
  };
  const [formInput, setFormInput] = useState({
    principal: 0,
    rate: 1,
    time: 1,
  });
  const [emiData, setEmidata] = useState({
    totalAmount: 0,
    principleAmount: 0,
    emi: 0,
  });
  const [pieData, setPieData] = useState({
    datasets: [
      {
        data: [300, 50],
        backgroundColor: ["#143C74", "#F47C34"],
        hoverOffset: 4,
      },
    ],
  });

  const calculateEMI = (principal, rate, tenureYears) => {
    const monthlyRate = rate / 12 / 100;
    const tenureMonths = tenureYears * 12;
    const emi =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths)) /
      (Math.pow(1 + monthlyRate, tenureMonths) - 1);
    const totalAmount = emi * tenureMonths;

    return {
      totalAmount: totalAmount.toFixed(2),
      principleAmount: principal,
      emi: emi.toFixed(2),
    };
  };

  useEffect(() => {
    // console.log("a");
    const { totalAmount, principleAmount, emi } = calculateEMI(
      formInput.principal,
      formInput.rate,
      formInput.time
    );
    setEmidata({ ...emiData, totalAmount, principleAmount, emi });
    setPieData({
      datasets: [
        {
          data: [emi, totalAmount],
          backgroundColor: ["#F47C34", "#143C74"],
          hoverOffset: 4,
        },
      ],
    });

    const amortizationData = calculateAmortizationSchedule(
      formInput.principal,
      formInput.rate,
      formInput.time
    );

    updateAmortizationTableData({
      headings: [
        "Month",
        "Opening Balance",
        "EMI",
        "Principal Repaid",
        "Interest Paid",
        "Closing Balance",
      ],
      rows: amortizationData.map((item) => [
        item.month,
        item.openingBalance,
        item.emi,
        item.principalRepaid,
        item.interestPaid,
        item.closingBalance,
      ]),
    });
  }, [formInput]);

  const HandleOnchange = (e) => {
    const { name, value } = e.target;

    let minVal, maxVal;

    // Define validation rules based on the input name
    switch (name) {
      case "principal":
        minVal = 0;
        maxVal = 50000000;
        break;
      case "rate":
        minVal = 0;
        maxVal = 35;
        break;
      case "time":
        minVal = 0;
        maxVal = 35;
        break;
      default:
        minVal = 0;
        maxVal = 0;
    }

    const sanitizedValue = isNaN(parseFloat(value))
      ? formInput[name]
      : parseFloat(value);
    const clampedValue = Math.min(Math.max(sanitizedValue, minVal), maxVal);

    setFormInput((prevFormInput) => ({
      ...prevFormInput,
      [name]: clampedValue,
    }));
  };

  return (
    <div className="flex flex-col items-center" id="calculator">
      <div className="flex flex-col md:flex-row justify-center items-stretch w-full ">
        <div className="flex w-11/12 mx-auto items-center flex-col md:w-[50%] md:mr-10 bg-white shadow-md rounded-lg  bg-opacity-90 p-2 h-[720px] sm:h-auto">
          <ul className="flex flex-row gap-6 w-full mx-10 border-b-2">
            <li className="p-2 cursor-pointer border-b-2 border-logo-orange">
              <div className="flex flex-row w-20 gap-2 items-center justify-center">
                <img
                  className="h-6"
                  src="https://cdn-icons-png.flaticon.com/128/5254/5254175.png"
                  alt="loancalv"
                />
                <p className="text-sm text-logo-blue leading-3 text-center">
                  Calculate your EMI
                </p>
              </div>
            </li>
          </ul>
          <div className="mt-2 flex flex-col sm:flex-row w-full gap-2 h-[400px] sm:h-[360px]">
            <div className="w-full sm:w-[60%] p-2 py-6 flex flex-col gap-5 bg-white shadow-md rounded-lg">
              <InputSlider
                name="principal"
                label="Loan Amount"
                onChange={HandleOnchange}
                value={formInput.principal}
                step={1000}
                min={0}
                max={50000000}
                minval="0"
                maxval="5 Cr."
              />

              <InputSlider
                label="Interest Rate(% P.A.)"
                name="rate"
                onChange={HandleOnchange}
                value={formInput.rate}
                step={0.1}
                min={1}
                max={35}
                minval="1 %"
                maxval="35 %"
              />
              <InputSlider
                name="time"
                label="Tenure (year)"
                onChange={HandleOnchange}
                value={formInput.time}
                step={0.5}
                min={1}
                max={35}
                minval="1 year"
                maxval="35 year"
              />
            </div>
            <div className="w-full sm:w-1/2 p-2 py-6 flex flex-col items-center justify-between bg-white shadow-md rounded-lg">
              <div className="h-2/3 bg-logo-orange bg-opacity-10 w-full flex items-center justify-center gap-2 px-3 py-6 rounded-sm shadow-sm">
                <div className="w-1/2">
                  <Doughnut data={pieData} />
                </div>
                <div className="w-1/2 flex flex-col gap-2">
                  <div className="w-full">
                    <p className="text-xs leading-5 text-slate-600">
                      Principal Amount
                      <span className="block text-xl leading-4">
                        {emiData.principleAmount}
                      </span>
                    </p>
                  </div>
                  <div className="w-full">
                    <p className="text-xs leading-5 text-logo-orange">
                      EMI Per Month
                      <span className="block text-xl leading-4">
                        {emiData.emi}
                      </span>
                    </p>
                  </div>
                  <div className="w-full">
                    <p className="text-xs leading-5 text-logo-blue">
                      Total Amount
                      <span className="block text-xl leading-4">
                        {emiData.totalAmount}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-full flex flex-col p-1 gap-2">
                <button
                  className="w-full bg-logo-orange text-white rounded p-1"
                  onClick={NavigateToLink}
                >
                  Apply Now
                </button>
                {isloginModalOpen && (
                  <LoginModal
                    isloginModalOpen={isloginModalOpen}
                    closeLoginModel={() => setIsloginModalOpen(false)}
                  />
                )}
                <p className="text-xs">
                  <span className="font-extrabold">Note: </span>
                  Please be advised that the calculator provided in this app is
                  intended for informational purposes only.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="w-11/12 md:w-[50%] h-full mt-4 md:mt-0 flex items-stretch">
          <EnquieryForm />
        </div>
      </div>
    </div>
  );
};

export default CalculatorEnquiery;
